import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { articles, articleOrder } from '../data/articles';
import { ArrowRight, Clock, Calendar } from 'lucide-react';

const pageContent = {
  en: {
    eyebrow: 'Engineering Insights',
    title: 'Engineering Notes on Production Systems',
    subtitle: 'Technical depth on architecture, infrastructure, and engineering decisions that matter.',
    featured: 'Selected Notes',
    archive: 'Archive',
    readMore: 'Read Note',
    minRead: 'min read'
  },
  tr: {
    eyebrow: 'Mühendislik Notları',
    title: 'Production Sistemleri Üzerine Mühendislik Notları',
    subtitle: 'Mimari, altyapı ve önemli mühendislik kararları hakkında teknik derinlik.',
    featured: 'Seçili Notlar',
    archive: 'Arşiv',
    readMore: 'Notu Oku',
    minRead: 'dk okuma'
  },
  de: {
    eyebrow: 'Engineering Insights',
    title: 'Engineering-Notizen zu Produktionssystemen',
    subtitle: 'Technische Tiefe zu Architektur, Infrastruktur und wichtigen Engineering-Entscheidungen.',
    featured: 'Ausgewählte Notizen',
    archive: 'Archiv',
    readMore: 'Notiz lesen',
    minRead: 'Min. Lesezeit'
  }
};

export function InsightsPage() {
  const { currentLanguage } = useLanguage();
  const t = pageContent[currentLanguage] || pageContent.en;

  const featuredArticles = articleOrder.filter(id => articles[id]?.featured);
  const regularArticles = articleOrder.filter(id => !articles[id]?.featured);

  const getText = (obj) => obj?.[currentLanguage] || obj?.en || '';

  return (
    <>
      <SEO page="insights" />
      <Navbar />
      
      <div className="min-h-screen">
        {/* Dark Hero Section */}
        <section 
          className="pt-32 pb-12 md:pt-36 md:pb-14 relative"
          style={{ 
            background: 'linear-gradient(180deg, #121821 0%, #0E1319 100%)',
            minHeight: '420px'
          }}
        >
          {/* Subtle grid pattern */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}
          />
          
          <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8">
            <span 
              className="text-xs uppercase tracking-widest mb-4 block"
              style={{ color: '#14B8A6', letterSpacing: '0.15em' }}
            >
              {t.eyebrow}
            </span>
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight mb-4"
              style={{ color: '#FFFFFF', letterSpacing: '-0.02em' }}
              data-testid="insights-page-title"
            >
              {t.title}
            </h1>
            <p 
              className="text-base md:text-lg max-w-2xl mb-6"
              style={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              {t.subtitle}
            </p>
            {/* Curated note */}
            <p 
              className="text-xs uppercase tracking-wider"
              style={{ 
                color: 'rgba(255, 255, 255, 0.4)', 
                fontFamily: 'JetBrains Mono, monospace',
                letterSpacing: '0.1em'
              }}
            >
              {currentLanguage === 'tr' ? 'Datex mühendislik ekibinden teknik notlar.' : 
               currentLanguage === 'de' ? 'Technische Notizen vom Datex Engineering-Team.' :
               'Technical notes from the Datex engineering team.'}
            </p>
          </div>
        </section>

        {/* Light Content Section */}
        <div className="bg-white pb-20">
          {/* Subtle background */}
          <div 
            className="fixed inset-0 z-0 pointer-events-none opacity-30"
            style={{
              background: 'radial-gradient(ellipse at 50% 0%, rgba(15, 118, 110, 0.08) 0%, transparent 50%)'
            }}
          />

          <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 pt-16 md:pt-20">

          {/* Featured Articles */}
          {featuredArticles.length > 0 && (
            <div className="mb-16">
              <div className="space-y-8">
                {featuredArticles.map((articleId) => {
                  const article = articles[articleId];
                  return (
                    <Link
                      key={articleId}
                      to={`/insights/${article.slug}`}
                      data-testid={`insight-featured-${articleId}`}
                      className="group block rounded-2xl overflow-hidden transition-all duration-500"
                      style={{ 
                        background: 'linear-gradient(180deg, rgba(15, 118, 110, 0.08) 0%, rgba(15, 118, 110, 0.02) 100%)',
                        border: '1px solid rgba(15, 118, 110, 0.15)'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(15, 118, 110, 0.3)'}
                      onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(15, 118, 110, 0.15)'}
                    >
                      <div className="p-6 md:p-8 lg:p-10">
                        {/* Top Row */}
                        <div className="flex flex-wrap items-center gap-3 mb-6">
                          <span 
                            className="text-[10px] uppercase tracking-wider px-2 py-1 rounded"
                            style={{ 
                              background: 'rgba(15, 118, 110, 0.1)',
                              color: '#0F766E'
                            }}
                          >
                            {t.featured}
                          </span>
                          <span 
                            className="text-xs uppercase tracking-wider"
                            style={{ color: 'var(--text-on-light-muted)' }}
                          >
                            {getText(article.category)}
                          </span>
                        </div>

                        {/* Title - min-height for grid alignment */}
                        <h2 
                          className="text-2xl md:text-3xl font-medium tracking-tight mb-4 group-hover:text-[#0F766E] transition-colors duration-300 min-h-[2.5em] md:min-h-[2em]"
                          style={{ color: 'var(--text-on-light-primary)', lineHeight: '1.25' }}
                        >
                          {getText(article.title)}
                        </h2>

                        {/* Excerpt */}
                        <p 
                          className="text-base mb-6 max-w-3xl"
                          style={{ color: 'var(--text-on-light-muted)' }}
                        >
                          {getText(article.excerpt)}
                        </p>

                        {/* Meta Row */}
                        <div className="flex flex-wrap items-center gap-6">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" style={{ color: 'var(--text-on-light-subtle)' }} />
                            <span className="text-sm" style={{ color: 'var(--text-on-light-subtle)' }}>
                              {article.readTime}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" style={{ color: 'var(--text-on-light-subtle)' }} />
                            <span className="text-sm" style={{ color: 'var(--text-on-light-subtle)' }}>
                              {article.publishedAt}
                            </span>
                          </div>
                          <span 
                            className="inline-flex items-center gap-2 text-sm font-medium ml-auto group-hover:gap-3 transition-all"
                            style={{ color: '#0F766E' }}
                          >
                            {t.readMore}
                            <ArrowRight className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Archive */}
          {regularArticles.length > 0 && (
            <div className="mt-16 pt-8" style={{ borderTop: '1px solid rgba(15, 118, 110, 0.1)' }}>
              <h2 
                className="text-xs uppercase mb-8 font-medium"
                style={{ color: 'var(--text-on-light-subtle)', letterSpacing: '0.12em' }}
              >
                {t.archive}
              </h2>
              <div className="space-y-4">
                {regularArticles.map((articleId) => {
                  const article = articles[articleId];
                  return (
                    <Link
                      key={articleId}
                      to={`/insights/${article.slug}`}
                      data-testid={`insight-${articleId}`}
                      className="group block p-5 rounded-xl transition-all duration-300"
                      style={{ 
                        background: '#F8FAFB',
                        border: '1px solid rgba(15, 118, 110, 0.08)'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(15, 118, 110, 0.2)'}
                      onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(15, 118, 110, 0.08)'}
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex-1">
                          {/* Category */}
                          <span 
                            className="text-[10px] uppercase tracking-wider mb-2 block"
                            style={{ color: '#0F766E' }}
                          >
                            {getText(article.category)}
                          </span>
                          
                          {/* Title */}
                          <h3 
                            className="text-lg font-medium mb-2 group-hover:text-[#0F766E] transition-colors"
                            style={{ color: 'var(--text-on-light-primary)' }}
                          >
                            {getText(article.title)}
                          </h3>
                          
                          {/* Excerpt */}
                          <p 
                            className="text-sm line-clamp-2"
                            style={{ color: 'var(--text-on-light-muted)' }}
                          >
                            {getText(article.excerpt)}
                          </p>
                        </div>

                        {/* Meta */}
                        <div className="flex items-center gap-4 md:flex-col md:items-end md:gap-2">
                          <span className="text-xs" style={{ color: 'var(--text-on-light-subtle)' }}>
                            {article.readTime}
                          </span>
                          <ArrowRight 
                            className="w-4 h-4 group-hover:translate-x-1 transition-transform" 
                            style={{ color: '#0F766E' }} 
                          />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}

export default InsightsPage;
